import { useEffect, useState, Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import SVG from 'react-inlinesvg'
import { useContextMotifGenerator } from '../../../contexts/motifGeneratorContext'
import { MotifGeneratorType } from '../types'
import { cn } from '../../../utils/cn'

const shapeTextClassName =
  'mb-[10px] flex w-[81px] items-center justify-center overflow-visible whitespace-nowrap'

// keep in sync with the animation durations in the class names below
const ANIMATION_DURATION = 0.5

export enum ExportLayoutModalResponse {
  CANCEL = 'CANCEL',
  SINGLE = 'SINGLE',
  TILES = 'TILES',
}

type Props = {
  responseHandlerFn: (response: ExportLayoutModalResponse) => false | void
  setExportInvoked: Dispatch<SetStateAction<boolean>>
  setExportModal: Dispatch<SetStateAction<boolean>>
}

const ExportLayoutModal = ({ responseHandlerFn, setExportInvoked, setExportModal }: Props) => {
  const [shouldHide, setShouldHide] = useState(false)
  const { setLoading, setStartExport, selectedGeneratorType } = useContextMotifGenerator()
  const { t } = useTranslation()

  const isBigTileGenerator =
    selectedGeneratorType === MotifGeneratorType.PENROSE ||
    selectedGeneratorType === MotifGeneratorType.MAP

  useEffect(() => {
    if (!shouldHide) return
    setTimeout(() => {
      setExportInvoked(false)
      setExportModal(false)
    }, ANIMATION_DURATION * 1000)
  }, [shouldHide])

  const hide = async () => {
    setLoading(false)
    setShouldHide(true)
    setExportModal(false)
    setExportInvoked(false)
  }

  return (
    <div
      className={cn(
        'absolute left-0 top-0 z-[1001] h-full w-full animate-[simple-fade-in_0.5s_forwards] bg-black70 [&.hide]:animate-[simple-fade-out_0.5s_forwards]',
        { hide: shouldHide },
      )}
      onClick={() => hide()}
    >
      <div
        className={cn(
          'absolute left-1/2 top-1/2 animate-[fade-in-y30-center_0.5s_forwards] rounded-[70px] bg-white pb-[70px] pl-[70px] pr-[70px] pt-[50px] text-[20px] [&.hide]:animate-[fade-out-y30-center_0.5s_forwards]',
          { hide: shouldHide },
        )}
        onClick={e => e.stopPropagation()}
      >
        <p className="mt-0 text-center text-[30px] font-bold">{t('generatorDownloadTitle')}</p>
        <div className="flex justify-around gap-[80px]">
          <div
            className={cn('flex flex-col items-center gap-[10px] p-0', {
              'justify-center': isBigTileGenerator,
            })}
          >
            {isBigTileGenerator ? (
              <span className={shapeTextClassName}>{t('generatorDownloadShapeFragment')}</span>
            ) : (
              <span className={shapeTextClassName}>{t('generatorDownloadShapeSingle')}</span>
            )}
            <button
              className={cn('relative h-[81px] w-[81px] border-none bg-red', {
                '[transform:scale(0.9)]': isBigTileGenerator,
              })}
              type="button"
              onClick={() => {
                setStartExport(true)
                responseHandlerFn(ExportLayoutModalResponse.SINGLE)
                setShouldHide(true)
              }}
            >
              <SVG
                src="/icons/dotted_down_arrow.svg"
                className="absolute left-1/2 top-1/2 h-[70%] w-[70%] text-white [transform:translate(-50%,-50%)]"
              />
            </button>
          </div>
          {isBigTileGenerator ? (
            <div className="flex flex-col items-center gap-[10px] p-0">
              <span className={shapeTextClassName}>{t('generatorDownloadShapeBigTile')}</span>
              <button
                className="relative h-[81px] w-[81px] border-none bg-red"
                type="button"
                onClick={() => {
                  setShouldHide(true)
                  responseHandlerFn(ExportLayoutModalResponse.TILES)
                  setTimeout(() => {
                    setStartExport(true)
                  }, 50)
                }}
              >
                <SVG
                  src="/icons/dotted_down_arrow.svg"
                  className="absolute left-1/2 top-1/2 h-[70%] w-[70%] text-white [transform:translate(-50%,-50%)]"
                />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-[10px] p-0">
              <span className={shapeTextClassName}>{t('generatorDownloadShapeTiles')}</span>
              <button
                className="relative h-[81px] w-[81px] border-none bg-red bg-[length:100%_2px,100%_2px,2px_100%,2px_100%] bg-[position:0_33.3333%,0_66.6666%,33.3333%_0,66.6666%_0] bg-no-repeat [background-image:linear-gradient(to_bottom,#FFFFFF_100%,transparent_0),linear-gradient(to_bottom,#FFFFFF_100%,transparent_0),linear-gradient(to_right,#FFFFFF_100%,transparent_0),linear-gradient(to_right,#FFFFFF_100%,transparent_0)]"
                type="button"
                onClick={() => {
                  setShouldHide(true)
                  responseHandlerFn(ExportLayoutModalResponse.TILES)
                  setTimeout(() => {
                    setStartExport(true)
                  }, 50)
                }}
              >
                <SVG
                  src="/icons/dotted_down_arrow.svg"
                  className="absolute left-1/2 top-1/2 h-[70%] w-[70%] text-white [filter:drop-shadow(3px_0_0_#E60050)_drop-shadow(-3px_0_0_#E60050)_drop-shadow(0_3px_0_#E60050)_drop-shadow(0_-3px_0_#E60050)] [transform:translate(-50%,-50%)]"
                />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default ExportLayoutModal
